import type { GroupParentAttributeOptionSet } from "@/components/configurator/global-option/types";
import { getDependentParentAttrCodes } from "@/components/configurator/global-option/helper";
import { updateSelectionsWithMatrix } from "@/components/configurator/qm/grid-layout/cell/body-cells/body-attribute-select.logic";
import { addFilter, applyFilters, type FilterCallback } from "@/extensions/hooks";
import type { DropdownState } from "@/extensions/hook-map";
import type { GlobalOptions } from "@/utils/form-data-manager";
import { isSameText } from "@/rules/helpers";

// In an open body cell dropdown, selects the option of any attribute that has nothing selected
// and only one usable option: when the dropdown opens, and after each selection for attributes
// that selection left with one (so an option the user clears is not re-selected).
// Only the group's main matrix-filtered parent is touched: groups without a matrix (e.g. Finish)
// and secondary parents (e.g. Kickplates Paint Grip, whose lone "SS" option is an alternative
// to the paint grip colors, not a forced choice) are left alone.

const NAMESPACE = "duke/plugins/auto-select-single-option";
const NONE_LABELS = ["None", "No"];

// The main parent is named "<Group name> Default", e.g. "Kickplates Default"
const isMainParent = (parentKey: string, groupName: string) =>
  isSameText(parentKey, `${groupName} Default`);

// Options left usable by both the matrix (isDisabled) and the "attribute.options" rules
const usableOptions = (
  child: any,
  parentKey: string,
  groupId: string,
  optionSet: GroupParentAttributeOptionSet,
  picks: GlobalOptions[],
) =>
  applyFilters("attribute.options", child.labelOptions.options, {
    scope: "body",
    picks,
    groupName: optionSet.get(groupId)?.groupName ?? "",
    parentName: parentKey,
    attributeCode: child.attributeCode,
    optionSet,
  }).filter((option) => !option.isDisabled && String(option.value ?? "") !== "");

const usableCountIn = (
  state: DropdownState,
  parentKey: string,
  attributeCode: string,
  groupId: string,
) => {
  const child = state.optionSet
    .get(groupId)
    ?.parents.get(parentKey)
    ?.child.find((attribute) => attribute.attributeCode === attributeCode);
  return child ? usableOptions(child, parentKey, groupId, state.optionSet, state.picks).length : 0;
};

const setPick = (picks: GlobalOptions[], attributeCode: string, value: string, label: string) =>
  picks.map((pick) => (pick.key === attributeCode ? { ...pick, value, label } : pick));

export const autoSelectSingleOption: FilterCallback<"body.dropdownUpdated"> = (
  value,
  { groupId, parentMatrixMap, previous },
) => {
  let { optionSet, picks } = value;
  const attempted = new Set<string>();

  // On open everything counts as new; after a change only attributes that change left with one.
  // An attribute the user just cleared already had one usable option, so it is not re-selected.
  const becameSingle = (parentKey: string, attributeCode: string) =>
    !previous || usableCountIn(previous, parentKey, attributeCode, groupId) !== 1;

  const findNext = () => {
    const group = optionSet.get(groupId);
    const parents = Array.from(group?.parents.entries() ?? []);
    for (const [parentKey, parent] of parents) {
      const matrix = parentMatrixMap.get(parentKey);
      if (!matrix || !isMainParent(parentKey, group?.groupName ?? "")) continue;

      for (const child of parent.child as any[]) {
        if (!child.isProduct || child.selectedOption?.value || attempted.has(child.attributeCode)) {
          continue;
        }
        const usable = usableOptions(child, parentKey, groupId, optionSet, picks);
        if (usable.length === 1 && becameSingle(parentKey, child.attributeCode)) {
          return { parentKey, child, option: usable[0], matrix };
        }
      }
    }
    return null;
  };

  // Repeats because one selection can narrow another attribute down to a single option
  for (let next = findNext(); next; next = findNext()) {
    const { parentKey, child, option, matrix } = next;
    attempted.add(child.attributeCode);

    // Same as a user pick: choosing None/No clears the picks of its dependent parents
    if (NONE_LABELS.includes(option.label)) {
      const cleared = getDependentParentAttrCodes(optionSet, groupId, parentKey);
      picks = picks.map((pick) => (cleared.includes(pick.key) ? { ...pick, value: "", label: "" } : pick));
    }

    optionSet = updateSelectionsWithMatrix(optionSet, option, child, parentKey, groupId, matrix);
    picks = setPick(picks, child.attributeCode, String(option.value), option.label);
  }

  return optionSet === value.optionSet ? value : { optionSet, picks };
};

export const registerAutoSelectSingleOption = () =>
  addFilter("body.dropdownUpdated", NAMESPACE, autoSelectSingleOption);
